// React.lazy that survives a new deploy. Each deploy replaces the hashed page files, so a tab
// opened before the deploy asks for a file that no longer exists and the import fails.
// Then we reload once to get the new version, instead of leaving a blank page.
import { lazy, type ComponentType } from "react";

const KEY = "semafor-page-reload";
const WINDOW_MS = 30_000;

function lastReload(): number {
  try {
    return Number(sessionStorage.getItem(KEY) ?? 0);
  } catch {
    return Date.now(); // no storage: never reload, show the error instead
  }
}

export function lazyPage<T extends ComponentType<any>>(load: () => Promise<{ default: T }>) {
  return lazy(() =>
    load().catch((e: unknown) => {
      // a second failure right after a reload is a real error: let the error boundary show it
      if (Date.now() - lastReload() < WINDOW_MS) throw e;
      try {
        sessionStorage.setItem(KEY, String(Date.now()));
      } catch {
        throw e;
      }
      window.location.reload();
      return new Promise<never>(() => undefined); // keep the loading screen until the reload
    }),
  );
}
